/**
 * Every string one credited person's page renders.
 *
 * The biography lives in the form and **nowhere else**. An earlier pass printed
 * it in the head as a lede too: the same text in two places on one screen is
 * two places to edit and one to forget. The head carries identity, the form
 * carries the fields, and the panel edge is the boundary between «what this is»
 * and «what you may change».
 */
export const copy = {
  documentTitle: (name: string) => `${name} — وحدة تحكّم شاهدني`,

  head: {
    openPublicPage: 'افتح صفحته العامّة',
  },

  fields: {
    heading: 'البيانات العامّة',
    context:
      'الاسم والسيرة والصورة تظهر في صفحته العامّة. أمّا أعماله وأدوارها فتُحرَّر من صفحة كلّ عنوان، في قسم «المنسوبون».',
    requiredNote: 'الحقول المؤشَّرة بـ',
    requiredNoteTail: ' مطلوبة.',
    name: {
      label: 'اسم العرض',
      required: 'أدخل اسم العرض.',
    },
    biography: {
      label: 'السيرة',
      hint: 'نصّ عاديّ بلا تنسيق. سطران أو ثلاثة تكفي.',
    },
    /**
     * The portrait control.
     *
     * The gate copy is read from the upload policy rather than written down —
     * the accepted formats, the size cap and the minimum geometry are all
     * operator-owned configuration, and a number typed here would be a second
     * statement of a value the service already publishes.
     */
    portrait: {
      title: 'الصورة',
      body: 'تظهر في صفحته العامّة وبجانب اسمه في طاقم كلّ عنوان. بلا صورة يظهر وجهٌ محايد.',
      choose: 'اختر صورة',
      replace: 'استبدل الصورة',
      remove: 'أزل الصورة',
      alt: (name: string) => `صورة ${name}`,
      none: 'لا صورة بعد.',
      uploading: 'جارٍ رفع الصورة…',
      uploaded: 'رُفعت الصورة.',
      failed: 'تعذّر رفع الصورة. أعد المحاولة.',
      /** Both halves come from `GET /uploadPolicy?subjectType=CREDIT_PROFILE`. */
      hint: (formats: string, maxMb: string, minWidth: string, minHeight: string) =>
        `${formats} — حتّى ${maxMb} ميغابايت، ولا تقلّ عن ${minWidth}×${minHeight} بكسل. تُقصّ الصورة مربّعةً من وسطها.`,
    },
    save: 'احفظ',
    cancel: 'ألغِ',
    saved: {
      title: 'حُفظت البيانات',
      /** The public profile is served through an ISR cache — «الآن» overstated it. */
      body: 'حُفظ الاسم والسيرة. صفحته العامّة تُحدَّث خلال دقائق قليلة، فصفحات الموقع تُقدَّم من ذاكرة مؤقّتة تتجدّد على فترات.',
    },
  },

  credits: {
    heading: 'النِّسَب',
    context:
      'الأعمال المسجّلة لهذا المنسوب، للاطّلاع فقط. تُضاف النسبة ودورها من صفحة العنوان نفسه — افتح العنوان من قائمة سطره.',
    caption: (name: string) => `النِّسَب المسجّلة لـ${name}، ودوره في كلّ عمل`,
    columns: {
      title: 'العنوان',
      type: 'النوع',
      role: 'الدور',
      publishedAt: 'تاريخ النشر',
      actions: 'أفعال النسبة',
    },
    /** A title still in draft has no publish date, and the cell says so. */
    unpublished: 'لم يُنشر بعد',
    rowMenu: {
      label: (role: string, title: string) => `أفعال نسبة «${role}» عن «${title}»`,
      openTitle: 'افتح العنوان',
    },
    /**
     * Both figures are counted from what was read, never written down: a
     * hand-written total under a table that edits itself is a lie waiting for
     * its first edit.
     */
    count: {
      creditsWord: 'نِسَب على',
      titlesWord: 'عناوين.',
      note: 'العنوان الذي له دوران يظهر في سطرين.',
      /** Said when the read hit the service's page ceiling. */
      more: 'يُعرض ما تسعه الصفحة الأولى من النِّسَب.',
    },
    states: {
      loading: 'جارٍ تحميل النِّسَب',
      empty: 'لا نسبة لهذا المنسوب بعد. تُضاف النسبة من صفحة العنوان، في قسم «الطاقم».',
    },
  },
} as const;
